import React from 'react';

import {AnalyticsParameterValueOperation} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import ParameterPillList from 'insights/parameters/ParameterPillList';
import sortedBy from 'util/sortedBy';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {EmbeddableParameterType} from '../EmbeddingConstants';
import {TagSchemaDashboardLinkTableQuery} from './__generated/TagSchemaDashboardLinkTableQuery';
import EmbeddedParameterPill from './EmbeddedParameterPill';

type AnalyticsParameterConfig = ExtractByTypename<
  NonNullable<
    ExtractByTypename<
      TagSchemaDashboardLinkTableQuery['tagSchema'],
      'TagSchemaNode'
    >['analyticsDashboardLink']
  >['originalAnalyticsDashboard'],
  'AnalyticsDashboardNode'
>['parameterConfigs'][number];

function findValueTemplate(
  config: AnalyticsParameterConfig,
  valueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>
): AnalyticsParameterValueTemplateGraphQL | undefined {
  return valueTemplates.find(
    (valueTemplate) => getAccessibleOrInaccessibleId(valueTemplate.config) === config.id
  );
}

export interface EmbeddedParameterPillListProps {
  embeddingType: EmbeddableParameterType;
  configs: Array<AnalyticsParameterConfig>;
  valueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>;
  editEmbeddedDashboard?: (config?: Pick<AnalyticsParameterConfig, 'id'>) => void;
}

export default function EmbeddedParameterPillList(props: EmbeddedParameterPillListProps): JSX.Element {
  const {editEmbeddedDashboard} = props;

  const sortedParameterConfigs = sortedBy(props.configs, [
    {
      // Move embedding item parameters to the front of the list
      accessor: (config) =>
        findValueTemplate(config, props.valueTemplates)?.valueOperation ===
        AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM
          ? '0'
          : '1',
    },
    {accessor: 'systemName', naturalSort: true}, // sort by parameter config name
  ]);

  return (
    <ParameterPillList>
      {sortedParameterConfigs.map((config) => (
        <EmbeddedParameterPill
          key={config.id}
          embeddingType={props.embeddingType}
          valueTemplate={findValueTemplate(config, props.valueTemplates)}
          config={config}
          onClick={
            editEmbeddedDashboard == null
              ? undefined
              : () => {
                  editEmbeddedDashboard(config);
                }
          }
        />
      ))}
    </ParameterPillList>
  );
}
